// --- React libraries ---
import React, { useState, useRef, useEffect } from "react";
import {
  Search,
  ChevronDown,
  Check,
  LogOut,
  SunMedium,
  Moon,
  Shield,
} from "lucide-react";

// --- shared components ---
import Button from "./ui/Button";
import Portal from "./ui/Portal";
import { TenantLogo3D } from "./TenantLogo3D";

// --- type files ---
import type { Tenant, ThemeMode } from "../types";
import type { HeaderPopoverType } from "./HeaderPopover";
import { useAuth } from "../../../contexts/AuthContext.js";

interface HeaderProps {
  isSidebarCollapsed: boolean;
  onOpenCommandPalette: () => void;
  currentTenant: Tenant;
  onTenantChange: (tenant: Tenant) => void;
  availableTenants: Tenant[];
  onTenantColorsChange: (
    colors: Partial<Pick<Tenant, "primaryColor" | "secondaryColor">>
  ) => void;
  themeMode: ThemeMode;
  onToggleThemeMode: () => void;
  onHelpHover: (_anchorRect: DOMRect) => void;
  onHelpToggle: (_anchorRect: DOMRect) => void;
  onHelpLeave: () => void;
  onNotificationsHover: (_anchorRect: DOMRect) => void;
  onNotificationsToggle: (_anchorRect: DOMRect) => void;
  onCreateHover: (_anchorRect: DOMRect) => void;
  onCreateToggle: (_anchorRect: DOMRect) => void;
  onHeaderPopoverLeave: () => void;
  activeHeaderPopover: HeaderPopoverType | null;
  commandQuery: string;
  onCommandQueryChange: (query: string) => void;
  isCommandOpen: boolean;
  onNavigate: (path: string) => void;
  commandInputRef?: React.RefObject<HTMLInputElement | null>;
}

const TENANT_MENU_WIDTH = 224; // w-56
const PROFILE_MENU_WIDTH = 288; // w-72

const Header: React.FC<HeaderProps> = ({
  isSidebarCollapsed,
  onOpenCommandPalette,
  currentTenant,
  onTenantChange,
  availableTenants,
  onTenantColorsChange,
  themeMode,
  onToggleThemeMode,
  onHelpHover: _onHelpHover,
  onHelpToggle: _onHelpToggle,
  onHelpLeave: _onHelpLeave,
  onNotificationsHover: _onNotificationsHover,
  onNotificationsToggle: _onNotificationsToggle,
  onCreateHover: _onCreateHover,
  onCreateToggle: _onCreateToggle,
  onHeaderPopoverLeave: _onHeaderPopoverLeave,
  activeHeaderPopover,
  commandQuery,
  onCommandQueryChange,
  isCommandOpen: _isCommandOpen,
  onNavigate,
  commandInputRef,
}) => {
  const { user, signOut } = useAuth();
  const [isTenantMenuOpen, setIsTenantMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [tenantSearchQuery, setTenantSearchQuery] = useState("");
  const tenantMenuRef = useRef<HTMLDivElement>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const tenantSearchInputRef = useRef<HTMLInputElement>(null);
  // These variables are used for styling - keep for future UI states
  void (activeHeaderPopover === "notifications");
  void (activeHeaderPopover === "create");

  // Get user display info from auth context
  // Fallback chain: user.name -> user.email -> 'Guest'
  // Note: user.email.split if user.name is missing/empty
  const userName = user?.name || (user?.email?.split("@")[0] ?? "Guest");
  const userRole = user?.role || "Member";

  const tenantTriggerRef = useRef<HTMLButtonElement>(null);
  const profileTriggerRef = useRef<HTMLButtonElement>(null);
  const [tenantMenuRect, setTenantMenuRect] = useState<DOMRect | null>(null);
  const [profileMenuRect, setProfileMenuRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedTenant =
        tenantMenuRef.current?.contains(target) ||
        tenantTriggerRef.current?.contains(target);
      const clickedProfile =
        profileMenuRef.current?.contains(target) ||
        profileTriggerRef.current?.contains(target);
      if (!clickedTenant) setIsTenantMenuOpen(false);
      if (!clickedProfile) setIsProfileMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const updateRects = () => {
      if (isTenantMenuOpen && tenantTriggerRef.current) {
        setTenantMenuRect(tenantTriggerRef.current.getBoundingClientRect());
      }
      if (isProfileMenuOpen && profileTriggerRef.current) {
        setProfileMenuRect(profileTriggerRef.current.getBoundingClientRect());
      }
    };
    updateRects();
    window.addEventListener("resize", updateRects);
    window.addEventListener("scroll", updateRects, true);
    return () => {
      window.removeEventListener("resize", updateRects);
      window.removeEventListener("scroll", updateRects, true);
    };
  }, [isTenantMenuOpen, isProfileMenuOpen]);

  // Focus search input when tenant menu opens, clear search when it closes
  useEffect(() => {
    if (isTenantMenuOpen && availableTenants.length > 5) {
      // Small delay to ensure DOM is ready
      setTimeout(() => tenantSearchInputRef.current?.focus(), 50);
    } else {
      setTenantSearchQuery("");
    }
  }, [isTenantMenuOpen, availableTenants.length]);

  const handleColorUpdate = (
    key: "primaryColor" | "secondaryColor",
    value: string
  ) => {
    if (!value) return;
    onTenantColorsChange({ [key]: value } as Partial<
      Pick<Tenant, "primaryColor" | "secondaryColor">
    >);
  };

  return (
    <>
      <header
        className={`
          fixed top-0 right-0 h-14 z-50 flex items-center justify-between px-6
          ${
            themeMode === "dark"
              ? "bg-background/90 backdrop-blur-md"
              : "bg-background"
          }
          transition-all duration-swm
          ${isSidebarCollapsed ? "left-[72px]" : "left-[240px]"}
        `}
      >
        {/* Left: Tenant Selector */}
        <div className="flex items-center gap-4">
          <div className="relative">
            <button
              ref={tenantTriggerRef}
              onClick={() => setIsTenantMenuOpen(!isTenantMenuOpen)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/50 hover:bg-accent transition-colors"
              data-testid="tenant-selector"
            >
              <TenantLogo3D
                logoUrl={currentTenant.logo}
                primaryColor={currentTenant.primaryColor}
                size={20}
              />
              <span className="text-sm font-medium" data-testid="tenant-name">
                {currentTenant.name}
              </span>
              <ChevronDown size={14} className="text-muted-foreground" />
            </button>
          </div>

          <span className="hidden sm:inline-block text-xs text-muted-foreground px-2 py-0.5 rounded-swm-pill bg-background border border-border">
            Free Plan
          </span>
        </div>

        {/* Right: Actions & Search */}
        <div className="flex items-center gap-3">
          {/* Inline Search Bar with Input */}
          <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-swm-control bg-accent/40 text-muted-foreground hover:text-foreground focus-within:bg-accent/60 focus-within:border-border/50 transition-colors border border-transparent min-w-[240px]">
            <Search size={14} />
            <input
              ref={commandInputRef}
              type="text"
              value={commandQuery}
              onChange={(e) => onCommandQueryChange(e.target.value)}
              onFocus={onOpenCommandPalette}
              placeholder="Search..."
              className="flex-1 bg-transparent outline-none text-xs text-foreground placeholder:text-muted-foreground/60"
              data-testid="search-input"
            />
            <kbd className="hidden lg:inline-flex h-5 items-center gap-1 rounded-sm border border-border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100">
              <span className="text-xs">⌘</span>K
            </kbd>
          </div>

          <div className="h-6 w-px bg-border/50 mx-1" />

          <div className="relative hidden sm:block">
            <button
              ref={profileTriggerRef}
              onClick={() => setIsProfileMenuOpen((prev) => !prev)}
              className="flex items-center gap-3 px-3 py-1.5 rounded-full bg-card hover:bg-accent/60 text-left transition-colors min-w-[170px]"
              aria-haspopup="menu"
              aria-expanded={isProfileMenuOpen}
              data-testid="profile-menu-button"
            >
              <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-purple-500 to-pink-500 shadow-sm" />
              <div className="leading-tight">
                <span className="block text-sm font-medium">{userName}</span>
                <span className="block text-xs text-foreground/70">
                  {userRole}
                </span>
              </div>
              <ChevronDown
                size={14}
                className="text-muted-foreground ml-auto"
              />
            </button>
          </div>
        </div>
      </header>

      {/* Tenant Dropdown via Portal */}
      {isTenantMenuOpen && tenantMenuRect && (
        <Portal>
          <div
            ref={tenantMenuRef}
            className="fixed z-[70] w-64 rounded-3xl border border-border bg-card backdrop-blur-sm shadow-[0_25px_70px_rgba(2,6,23,0.55)] overflow-hidden animate-in fade-in zoom-in-95"
            style={{
              top: tenantMenuRect.bottom + 8,
              left: Math.max(
                16,
                Math.min(
                  tenantMenuRect.left,
                  window.innerWidth - TENANT_MENU_WIDTH - 16
                )
              ),
            }}
          >
            <div className="px-4 py-3 border-b border-border/60">
              <p className="text-[10px] uppercase tracking-[0.4em] text-muted-foreground/80">
                Tenant
              </p>
              <p className="text-sm font-semibold text-foreground">
                Switch Workspace
              </p>
            </div>
            {availableTenants.length > 5 && (
              <div className="px-3 py-2 border-b border-border/40">
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-accent/40 text-muted-foreground focus-within:bg-accent/60 transition-colors">
                  <Search size={14} />
                  <input
                    ref={tenantSearchInputRef}
                    type="text"
                    value={tenantSearchQuery}
                    onChange={(e) => setTenantSearchQuery(e.target.value)}
                    placeholder="Search tenants..."
                    className="flex-1 bg-transparent outline-none text-xs text-foreground placeholder:text-muted-foreground/60"
                    data-testid="tenant-search-input"
                  />
                </div>
              </div>
            )}
            <div className="p-2 space-y-1 max-h-60 overflow-y-auto">
              {(() => {
                const filteredTenants = availableTenants.filter(
                  (tenant) =>
                    tenantSearchQuery === "" ||
                    tenant.name
                      .toLowerCase()
                      .includes(tenantSearchQuery.toLowerCase())
                );
                if (filteredTenants.length === 0) {
                  return (
                    <div className="px-3 py-4 text-center text-xs text-muted-foreground">
                      No tenants found
                    </div>
                  );
                }
                return filteredTenants.map((tenant) => (
                  <button
                    key={tenant.id}
                    onClick={() => {
                      onTenantChange(tenant);
                      setIsTenantMenuOpen(false);
                    }}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2 rounded-2xl text-sm transition-colors border
                      ${
                        currentTenant.id === tenant.id
                          ? "border-primary/50 bg-primary/10 text-primary"
                          : "border-transparent hover:border-border/70 text-foreground"
                      }
                    `}
                  >
                    <div
                      className="w-3 h-3 rounded-full shrink-0"
                      style={{ backgroundColor: tenant.primaryColor }}
                    />
                    <span className="flex-1 text-left truncate">
                      {tenant.name}
                    </span>
                    {currentTenant.id === tenant.id && (
                      <Check size={14} className="text-primary" />
                    )}
                  </button>
                ));
              })()}
            </div>
          </div>
        </Portal>
      )}

      {/* Profile Dropdown via Portal */}
      {isProfileMenuOpen && profileMenuRect && (
        <Portal>
          <div
            ref={profileMenuRef}
            className="fixed z-[70] w-72 rounded-3xl border border-border bg-card backdrop-blur-sm shadow-[0_25px_80px_rgba(2,6,23,0.55)] overflow-hidden animate-in fade-in zoom-in-95"
            style={{
              top: profileMenuRect.bottom + 8,
              left: Math.min(
                Math.max(16, profileMenuRect.left),
                window.innerWidth - PROFILE_MENU_WIDTH - 16
              ),
            }}
            data-testid="profile-menu"
          >
            <div className="px-4 py-3 border-b border-border/60">
              <p className="text-sm font-semibold">Profile & Theme</p>
              <p className="text-xs text-muted-foreground">
                Signed in as {userName}
              </p>
            </div>

            <div className="p-4 space-y-4">
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Brand Colors
                </p>
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                      <span
                        className="inline-flex h-4 w-4 rounded-full border border-border/60"
                        style={{ backgroundColor: currentTenant.primaryColor }}
                      />
                      Primary
                    </div>
                    <span className="text-xs font-mono px-2 py-1 rounded bg-muted/30">
                      {currentTenant.primaryColor}
                    </span>
                    <input
                      type="color"
                      value={currentTenant.primaryColor}
                      onChange={(e) =>
                        handleColorUpdate("primaryColor", e.target.value)
                      }
                      className="h-8 w-8 rounded-full border border-border/60 cursor-pointer bg-transparent"
                      aria-label="Select primary color"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                      <span
                        className="inline-flex h-4 w-4 rounded-full border border-border/60"
                        style={{
                          backgroundColor:
                            currentTenant.secondaryColor ?? "#94a3b8",
                        }}
                      />
                      Secondary
                    </div>
                    <span className="text-xs font-mono px-2 py-1 rounded bg-muted/30">
                      {currentTenant.secondaryColor ?? "#94a3b8"}
                    </span>
                    <input
                      type="color"
                      value={currentTenant.secondaryColor ?? "#94a3b8"}
                      onChange={(e) =>
                        handleColorUpdate("secondaryColor", e.target.value)
                      }
                      className="h-8 w-8 rounded-full border border-border/60 cursor-pointer bg-transparent"
                      aria-label="Select secondary color"
                    />
                  </div>
                </div>
              </div>

              <div className="pt-2 border-t border-border/60 space-y-2">
                {(userRole === "TENANT_ADMIN" || userRole === "ADMIN") && (
                  <button
                    onClick={() => {
                      onNavigate("admin-center");
                      setIsProfileMenuOpen(false);
                    }}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-accent/40 transition-colors"
                  >
                    <span className="flex items-center gap-2">
                      <Shield size={14} />
                      Admin Center
                    </span>
                  </button>
                )}

                <button
                  onClick={onToggleThemeMode}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-accent/40 transition-colors"
                >
                  <span className="flex items-center gap-2">
                    {themeMode === "dark" ? (
                      <SunMedium size={14} />
                    ) : (
                      <Moon size={14} />
                    )}
                    {themeMode === "dark" ? "Light Mode" : "Dark Mode"}
                  </span>
                </button>

                <Button
                  variant="ghost"
                  className="w-full justify-between text-sm text-muted-foreground hover:text-foreground"
                  onClick={() => signOut()}
                  data-testid="logout-button"
                >
                  Logout
                  <LogOut size={14} />
                </Button>
              </div>
            </div>
          </div>
        </Portal>
      )}
    </>
  );
};

export default Header;
